// Unified Product Data for Skytion Enterprises
// Single source of truth for all product information

export interface ProductImage {
  src: string;
  alt: string;
  caption?: string;
}

export interface ProductSpecs {
  dimension?: string;
  material: string;
  weight?: string;
  ply: string;
  gsm?: string;
  sheets?: string;
  pulls?: string;
  size?: string;
  mrp?: string;
  model?: string;
  fold?: string;
  type?: string;
  application: string[];
}

export interface Product {
  id: string;
  name: string;
  brand: 'SKYSUN' | 'SKYTION' | 'SKYTION';
  category: string;
  categoryLabel: string;
  subcategory?: string;
  slug: string;
  description: string;
  images: ProductImage[];
  specs: ProductSpecs;
  features: string[];
  useCases: string[];
  seoTitle: string;
  seoDescription: string;
}

export const productCategories = [
  { id: 'toilet-rolls', name: 'Toilet Rolls', label: 'Toilet Rolls', icon: 'toilet' },
  { id: 'napkins', name: 'Napkins & Single Tissue', label: 'Napkins', icon: 'napkin' },
  { id: 'box-tissue', name: 'Box & Facial Tissue', label: 'Box Tissue', icon: 'box' },
  { id: 'kitchen-towels', name: 'Kitchen Towels', label: 'Kitchen Towels', icon: 'kitchen' },
  { id: 'c-fold', name: 'C-Fold Tissue', label: 'C-Fold', icon: 'c-fold' },
  { id: 'm-fold', name: 'M-Fold Tissue', label: 'M-Fold', icon: 'm-fold' },
  { id: 'hrt-rolls', name: 'HRT Rolls', label: 'HRT Rolls', icon: 'hrt' },
  { id: 'mini-tissue', name: 'Mini Pocket Tissues', label: 'Pocket Tissues', icon: 'pocket' },
  { id: 'paper-cups', name: 'Paper Cups', label: 'Paper Cups', icon: 'cup' },
  { id: 'food-containers', name: 'Food Containers', label: 'Food Containers', icon: 'container' },
  { id: 'dining-tissue', name: 'Dining Tissue', label: 'Dining Tissue', icon: 'dining' },
  { id: 'hygiene-solutions', name: 'Hygiene Solutions', label: 'Hygiene Solutions', icon: 'hygiene' },
  { id: 'wooden-cutlery', name: 'Wooden Cutlery', label: 'Wooden Cutlery', icon: 'cutlery' },
];

export const products: Product[] = [
  // ==================== TOILET ROLLS ====================
  {
    id: 'toilet-roll-2ply-6pack',
    name: 'Skysun 2 Ply Toilet Roll (6 Pack)',
    brand: 'SKYSUN',
    category: 'toilet-rolls',
    categoryLabel: 'Toilet Rolls',
    subcategory: 'Premium Toilet Rolls',
    slug: 'skysun-2ply-toilet-roll',
    description: 'Luxury soft and natural toilet rolls crafted with 100% virgin pulp. Each pack contains 6 rolls with 200 sheets per roll (1200 sheets total). Ultra-soft, hygienic, and eco-friendly.',
    images: [
      { src: '/assets/skytion/products/toilet-roll-2.jpeg', alt: 'Skysun 2 Ply Toilet Roll 6 Pack Front' },
      { src: '/assets/skytion/products/toiletrollpack.jpeg', alt: 'Skysun Toilet Roll Pack Back with Details' },
      { src: '/assets/skytion/products/skysunpack.jpeg', alt: 'Skysun Product Range with Toilet Rolls' },
      { src: '/assets/skytion/products/skysunpack2.jpeg', alt: 'Skysun Complete Product Display' },
    ],
    specs: {
      model: 'SKY 102',
      ply: '2',
      sheets: '200 sheets per roll (1200 total)',
      dimension: '10 cm x 11 cm',
      weight: '480g per pack',
      mrp: '₹225.00',
      material: '100% Virgin Pulp',
      application: ['Household bathrooms', 'Office washrooms', 'Hotels', 'Healthcare facilities'],
    },
    features: [
      '2 Ply premium softness',
      '100% Hygienic',
      'Ultra-soft texture',
      'No trees cut - made from sugarcane and bamboo',
      'Biodegradable and eco-friendly',
      'FSC Certified',
      'Made in India',
      '6 in 1 value pack',
    ],
    useCases: [
      'Residential bathroom use',
      'Office and corporate washrooms',
      'Hotel and hospitality industry',
      'Healthcare facilities',
      'Educational institutions',
    ],
    seoTitle: 'Skysun 2 Ply Toilet Roll 6 Pack | Premium Bathroom Tissue',
    seoDescription: 'Buy Skysun 2 Ply toilet rolls (6 pack, 1200 sheets). Ultra-soft, hygienic, eco-friendly. Made from virgin pulp. MRP ₹225.',
  },
  {
    id: 'toilet-roll-premium',
    name: 'Premium Toilet Roll',
    brand: 'SKYSUN',
    category: 'toilet-rolls',
    categoryLabel: 'Toilet Rolls',
    subcategory: 'Premium Toilet Rolls',
    slug: 'premium-toilet-roll',
    description: 'Premium quality toilet roll with embossed flower pattern. Soft, absorbent, and gentle on skin.',
    images: [
      { src: '/assets/skytion/products/toiletb-paper.jpg', alt: 'Premium Toilet Rolls with Flower Pattern' },
      { src: '/assets/skytion/products/toilet-paper.png', alt: 'Toilet Roll Close-up' },
      { src: '/assets/skytion/products/toilet-paper-02.jpg', alt: 'Toilet Roll on Surface' },
    ],
    specs: {
      ply: '2',
      material: 'Virgin Pulp',
      application: ['Home', 'Office', 'Commercial'],
    },
    features: [
      'Embossed flower pattern',
      '2 Ply construction',
      'Superior absorbency',
      'Soft and gentle',
      'Septic safe',
    ],
    useCases: [
      'Daily household use',
      'Guest bathrooms',
      'Premium facilities',
    ],
    seoTitle: 'Premium Toilet Roll | Embossed Bathroom Tissue',
    seoDescription: 'High-quality embossed toilet rolls with flower pattern. 2 ply, ultra-absorbent, and septic safe.',
  },
  {
    id: 'toilet-roll-jumbo',
    name: 'Jumbo Toilet Roll',
    brand: 'SKYTION',
    category: 'toilet-rolls',
    categoryLabel: 'Toilet Rolls',
    subcategory: 'Jumbo Rolls',
    slug: 'jumbo-toilet-roll',
    description: 'Large jumbo toilet rolls for commercial and institutional use. Long-lasting and economical.',
    images: [
      { src: '/assets/skytion/products/toilet-paper-0001.jpg', alt: 'Jumbo Toilet Rolls' },
      { src: '/assets/skytion/products/360-degree-product.png', alt: '360 Degree View of Toilet Roll' },
    ],
    specs: {
      ply: '1-2',
      material: 'Recycled/Virgin Pulp',
      application: ['Commercial', 'Institutional', 'Industrial'],
    },
    features: [
      'Jumbo size for extended use',
      'Cost-effective',
      'High capacity',
      'Suitable for dispensers',
    ],
    useCases: [
      'Public restrooms',
      'Office buildings',
      'Shopping malls',
      'Schools and colleges',
    ],
    seoTitle: 'Jumbo Toilet Roll | Commercial Bathroom Tissue',
    seoDescription: 'Large jumbo toilet rolls for commercial use. Cost-effective, high capacity.',
  },
  {
    id: 'toilet-roll-eco',
    name: 'Eco-Friendly Toilet Roll',
    brand: 'SKYTION',
    category: 'toilet-rolls',
    categoryLabel: 'Toilet Rolls',
    subcategory: 'Eco Toilet Rolls',
    slug: 'eco-friendly-toilet-roll',
    description: 'Environmentally conscious toilet roll made from sustainable materials. Biodegradable and recyclable packaging.',
    images: [
      { src: '/assets/skytion/products/ecofriendly-toilet-paper-image.png', alt: 'Eco-Friendly Toilet Roll' },
      { src: '/assets/skytion/products/toilet-001.png', alt: 'Skysun Eco Toilet Roll Packaging' },
    ],
    specs: {
      ply: '2',
      material: 'Bamboo & Sugarcane Pulp',
      application: ['Eco-conscious homes', 'Green offices', 'Sustainable facilities'],
    },
    features: [
      'Made from renewable resources',
      'No trees cut',
      'Biodegradable',
      'Recyclable packaging',
      'Carbon footprint reduced',
      'FSC Certified',
    ],
    useCases: [
      'Environmentally conscious consumers',
      'Green buildings',
      'Sustainable businesses',
    ],
    seoTitle: 'Eco-Friendly Toilet Roll | Sustainable Bamboo Tissue',
    seoDescription: '100% eco-friendly toilet rolls made from bamboo and sugarcane. No trees cut.',
  },

  // ==================== NAPKINS & SINGLE TISSUE ====================
  {
    id: 'cloud-soft-napkins-1ply',
    name: 'Cloud Soft Napkins (1 Ply, 100 Pull)',
    brand: 'SKYTION',
    category: 'napkins',
    categoryLabel: 'Napkins & Single Tissue',
    subcategory: 'Premium Napkins',
    slug: 'cloud-soft-napkins',
    description: 'Cloud soft napkins made from responsibly sourced sugarcane fiber. 100% biodegradable, food safe, and skin friendly with excellent absorbency.',
    images: [
      { src: '/assets/skytion/products/napkin-1ply-100pull.jpeg', alt: 'Skytion Cloud Soft Napkins Front' },
      { src: '/assets/skytion/products/napking-backside-image.jpeg', alt: 'Cloud Soft Napkins Back with Details' },
      { src: '/assets/skytion/products/skytionnapkin.jpeg', alt: 'Skytion Napkin Package' },
    ],
    specs: {
      model: 'SKY 103',
      ply: '1',
      sheets: '100 (Approx)',
      dimension: '30cm x 30cm',
      mrp: '₹95.00',
      material: 'Sugarcane Fiber',
      application: ['Dining', 'Kitchen', 'Food service', 'Household'],
    },
    features: [
      '100% Biodegradable',
      'Food Safe & Skin Friendly',
      'No Trees Cut in Production',
      'Excellent Absorbency',
      'Soft, Strong and Perfect',
      'FSC Certified',
      'Made in India',
    ],
    useCases: [
      'Daily dining',
      'Kitchen cleaning',
      'Food service industry',
      'Restaurants and cafes',
      'Catering services',
    ],
    seoTitle: 'Cloud Soft Napkins 1 Ply 100 Pull | Skytion Enterprises',
    seoDescription: 'Buy Cloud Soft napkins (1 Ply, 100 pull, 30x30cm). 100% biodegradable, food safe.',
  },
  {
    id: 'skysun-premium-napkin',
    name: 'Skysun Premium Napkin',
    brand: 'SKYSUN',
    category: 'napkins',
    categoryLabel: 'Napkins & Single Tissue',
    subcategory: 'Premium Napkins',
    slug: 'skysun-premium-napkin',
    description: 'Premium napkins with Skysun branding. Absorbent, soft & strong, toxin-free, and hygienic.',
    images: [
      { src: '/assets/skytion/products/tisseu2.jpeg', alt: 'Skysun Premium Napkin Green' },
      { src: '/assets/skytion/products/tisseu-back-packing.jpeg', alt: 'Skysun Napkin Back' },
      { src: '/assets/skytion/products/napkin-image.jpeg', alt: 'Napkin Stack View' },
      { src: '/assets/skytion/products/tissueimage.jpeg', alt: 'Napkin Top View' },
    ],
    specs: {
      ply: '1-2',
      size: 'Big',
      material: 'Virgin Pulp',
      mrp: '₹65/-',
      application: ['Dining', 'Household', 'Commercial'],
    },
    features: [
      'Absorbent',
      'Soft & Strong',
      'Toxin Free',
      'Hygienic',
      'Recyclable',
      'Made in India',
    ],
    useCases: [
      'Daily household use',
      'Dining tables',
      'Kitchen cleaning',
      'Personal hygiene',
    ],
    seoTitle: 'Skysun Premium Napkin | Soft & Absorbent Tissue',
    seoDescription: 'Skysun premium napkins - absorbent, soft, toxin-free. Available in big size.',
  },
  {
    id: 'skysun-napkin-light',
    name: 'Skysun Napkin (Light Green)',
    brand: 'SKYSUN',
    category: 'napkins',
    categoryLabel: 'Napkins & Single Tissue',
    subcategory: 'Standard Napkins',
    slug: 'skysun-napkin-light',
    description: 'Light green packaged Skysun napkins. Premium quality with absorbent and soft properties.',
    images: [
      { src: '/assets/skytion/products/tissue-light.jpeg', alt: 'Skysun Napkin Light Green' },
      { src: '/assets/skytion/products/tissue.jpeg', alt: 'Skysun Napkin Side View' },
    ],
    specs: {
      ply: '1',
      material: 'Virgin Pulp',
      application: ['Household', 'Small businesses'],
    },
    features: [
      'Light and portable',
      'Absorbent',
      'Soft texture',
      'Hygienic packaging',
    ],
    useCases: [
      'Personal use',
      'Travel',
      'Small offices',
      'Shops and establishments',
    ],
    seoTitle: 'Skysun Napkin Light Green | Portable Tissue',
    seoDescription: 'Skysun light green napkins - portable, absorbent, and hygienic.',
  },
  {
    id: 'single-ply-tissue-stack',
    name: 'Single Ply Tissue (Stack)',
    brand: 'SKYTION',
    category: 'napkins',
    categoryLabel: 'Napkins & Single Tissue',
    subcategory: 'Bulk Tissue',
    slug: 'single-ply-tissue',
    description: 'Single ply tissue in bulk stack format. Ideal for commercial dispensers and high-traffic areas.',
    images: [
      { src: '/assets/skytion/products/1-ply-single-tissue.png', alt: 'Single Ply Tissue Stack' },
      { src: '/assets/skytion/products/1-ply-single-tisuue-op-2.jpg', alt: 'Single Ply Tissue Stack Close-up' },
      { src: '/assets/skytion/products/soft-tissue.png', alt: 'Soft Tissue Sheets' },
      { src: '/assets/skytion/products/spec-single-ply-tissue-paper.jpg', alt: 'Single Ply Tissue Sheet' },
    ],
    specs: {
      ply: '1',
      material: 'Virgin/Recycled Pulp',
      application: ['Commercial dispensers', 'Public restrooms', 'Offices'],
    },
    features: [
      'Bulk stack format',
      'Compatible with dispensers',
      'Cost-effective',
      'Good absorbency',
    ],
    useCases: [
      'Public restrooms',
      'Office buildings',
      'Shopping centers',
      'Healthcare facilities',
    ],
    seoTitle: 'Single Ply Tissue Stack | Commercial Hand Towels',
    seoDescription: 'Single ply tissue in bulk stack. Perfect for commercial dispensers.',
  },
  {
    id: '2ply-premium-napkin',
    name: '2 Ply Premium Napkin',
    brand: 'SKYSUN',
    category: 'napkins',
    categoryLabel: 'Napkins & Single Tissue',
    subcategory: 'Premium Napkins',
    slug: '2ply-premium-napkin',
    description: 'Premium 2 ply napkins with enhanced softness and durability. Pink packaging with Skysun branding.',
    images: [
      { src: '/assets/skytion/products/2-ply-tissue-paper.png', alt: '2 Ply Premium Napkins Pink' },
      { src: '/assets/skytion/products/skytion-single-tissue.png', alt: 'Skytion Blue Napkins' },
      { src: '/assets/skytion/products/skytion-tissue-packet.png', alt: 'Skytion Pink Packet' },
    ],
    specs: {
      ply: '2',
      material: 'Virgin Pulp',
      application: ['Premium dining', 'Hotels', 'Restaurants'],
    },
    features: [
      '2 Ply premium softness',
      'Enhanced durability',
      'Superior absorbency',
      'Elegant packaging',
    ],
    useCases: [
      'Fine dining',
      'Hotels and resorts',
      'Premium restaurants',
      'Special occasions',
    ],
    seoTitle: '2 Ply Premium Napkin | Luxury Dining Tissue',
    seoDescription: '2 ply premium napkins with enhanced softness. Perfect for fine dining.',
  },

  // ==================== BOX TISSUE & FACIAL TISSUE ====================
  {
    id: 'skysun-facial-tissue-box',
    name: 'Skysun Facial Tissue Box (2 Ply, 100 Pull)',
    brand: 'SKYSUN',
    category: 'box-tissue',
    categoryLabel: 'Box & Facial Tissue',
    subcategory: 'Facial Tissue Boxes',
    slug: 'skysun-facial-tissue-box',
    description: 'Premium facial tissue box with 2 ply, 100 pulls (200 sheets total). Ultra-soft, super-soft & skin safe. Sheet size 20cm x 20cm.',
    images: [
      { src: '/assets/skytion/products/box-tissue.jpeg', alt: 'Skysun Facial Tissue Box Blue Front' },
      { src: '/assets/skytion/products/box-tissue2.jpeg', alt: 'Skysun Facial Tissue Box Angle View' },
      { src: '/assets/skytion/products/20cmby20cm-tissue.jpeg', alt: 'Facial Tissue Box Back Details' },
    ],
    specs: {
      ply: '2',
      pulls: '100 Pulls (200 Sheets)',
      dimension: '20cm x 20cm',
      mrp: '₹85.00',
      material: '100% Virgin Paper',
      application: ['Home', 'Office', 'Car', 'Bedroom'],
    },
    features: [
      '2 Ply construction',
      '100% Virgin Paper',
      'Super-soft & Skin Safe',
      'Ultra Soft',
      'Biodegradable Product',
      'Indian Owned',
      'Recyclable Packaging',
    ],
    useCases: [
      'Home use - living room, bedroom',
      'Office desk',
      'Car glove compartment',
      'Personal hygiene',
      'Makeup removal',
    ],
    seoTitle: 'Skysun Facial Tissue Box 2 Ply 100 Pull | Premium Face Tissue',
    seoDescription: 'Skysun facial tissue box (2 Ply, 100 Pull, 20x20cm). 100% virgin paper, ultra-soft.',
  },
  {
    id: 'premium-facial-tissue-collection',
    name: 'Premium Facial Tissue Collection',
    brand: 'SKYSUN',
    category: 'box-tissue',
    categoryLabel: 'Box & Facial Tissue',
    subcategory: 'Facial Tissue Boxes',
    slug: 'premium-facial-tissue',
    description: 'Professional product photography showcasing Skytion ultra-soft facial tissues in multiple elegant packaging colors.',
    images: [
      { src: '/assets/skytion/products/professional-product-photography.png', alt: 'Skytion Facial Tissue Collection' },
      { src: '/assets/skytion/products/ultra-soft.png', alt: 'Skytion Ultra Soft Experience' },
    ],
    specs: {
      ply: '2',
      material: 'Premium Virgin Pulp',
      application: ['Premium homes', 'Hotels', 'Spas', 'Offices'],
    },
    features: [
      'Ultra-soft formula',
      'Premium packaging',
      'Multiple variants',
      'Gentle care',
      'High absorbency',
    ],
    useCases: [
      'Premium household use',
      'Hotel amenities',
      'Spa and wellness centers',
      'Executive offices',
    ],
    seoTitle: 'Premium Facial Tissue Collection | Skytion Ultra Soft',
    seoDescription: 'Skytion premium facial tissue collection. Ultra-soft, gentle care.',
  },

  // ==================== KITCHEN TOWELS ====================
  {
    id: 'kitchen-towel-roll',
    name: 'Kitchen Towel Roll',
    brand: 'SKYSUN',
    category: 'kitchen-towels',
    categoryLabel: 'Kitchen Towels',
    subcategory: 'Kitchen Paper Towels',
    slug: 'kitchen-towel',
    description: 'Premium kitchen towel rolls with textured surface for superior absorbency. Perfect for kitchen cleaning and food preparation.',
    images: [
      { src: '/assets/skytion/products/kitchen-tissue-towel-02.jpg', alt: 'Kitchen Towel Roll on Stand' },
      { src: '/assets/skytion/products/kitchen-tissue-towel.png', alt: 'Kitchen Towel Roll' },
      { src: '/assets/skytion/products/kitchen-towel-png.png', alt: 'Kitchen Towel Being Used' },
      { src: '/assets/skytion/products/paper-towel.jpg', alt: 'Kitchen Paper Towel' },
    ],
    specs: {
      ply: '2',
      dimension: '4 inch x 8 inch',
      weight: '240g',
      material: 'Virgin Pulp',
      application: ['Kitchen cleaning', 'Food preparation', 'Spill cleanup'],
    },
    features: [
      'Textured surface',
      'High absorbency',
      'Strong when wet',
      'Lint-free',
      'Food safe',
    ],
    useCases: [
      'Kitchen counter cleaning',
      'Drying dishes',
      'Food preparation',
      'Spill cleanup',
      'Grease absorption',
    ],
    seoTitle: 'Kitchen Towel Roll | Absorbent Paper Towels',
    seoDescription: 'Premium kitchen towel rolls with textured surface. High absorbency, strong when wet.',
  },
  {
    id: 'kitchen-towel-set',
    name: 'Kitchen Towel & Napkin Set',
    brand: 'SKYSUN',
    category: 'kitchen-towels',
    categoryLabel: 'Kitchen Towels',
    subcategory: 'Kitchen Paper Towels',
    slug: 'kitchen-towel-set',
    description: 'Complete kitchen towel and napkin set with modern holders. Elegant design for contemporary kitchens.',
    images: [
      { src: '/assets/skytion/products/kitchen-towel-tissue-paper-01.jpg', alt: 'Kitchen Towel Set with Holders' },
      { src: '/assets/skytion/products/kitchen-towel-and-tissue-paper.png', alt: 'Kitchen Towel Set' },
    ],
    specs: {
      ply: '2',
      material: 'Premium Virgin Pulp',
      application: ['Modern kitchens', 'Premium homes'],
    },
    features: [
      'Complete set with holders',
      'Modern design',
      'Premium quality',
      'Space-saving',
    ],
    useCases: [
      'Contemporary kitchens',
      'Premium households',
      'Gift sets',
    ],
    seoTitle: 'Kitchen Towel & Napkin Set | Modern Kitchen Essentials',
    seoDescription: 'Complete kitchen towel and napkin set with stylish holders.',
  },

  // ==================== C-FOLD TISSUE ====================
  {
    id: 'c-fold-tissue-commercial',
    name: 'C-Fold Tissue (Commercial)',
    brand: 'SKYTION',
    category: 'c-fold',
    categoryLabel: 'C-Fold Tissue',
    subcategory: 'C-Fold Hand Towels',
    slug: 'c-fold-tissue',
    description: 'Commercial grade C-fold hand towels for dispensers. Ideal for high-traffic public restrooms and commercial facilities.',
    images: [
      { src: '/assets/skytion/products/c-fold-tissue-01.jpg', alt: 'C-Fold Tissue Stack' },
      { src: '/assets/skytion/products/c-fold-tissue-paper.png', alt: 'C-Fold Tissue' },
      { src: '/assets/skytion/products/c-fold-napkin-500x500-removebg-preview.png', alt: 'C-Fold Napkin Stack' },
      { src: '/assets/skytion/products/c-fold-napkin-500x500.webp', alt: 'C-Fold Napkins' },
    ],
    specs: {
      ply: '1-2',
      fold: 'C-Fold',
      material: 'Virgin/Recycled Pulp',
      application: ['Public restrooms', 'Offices', 'Hospitals', 'Schools'],
    },
    features: [
      'C-fold design for dispensers',
      'High capacity',
      'Cost-effective',
      'Good absorbency',
      'Compatible with standard dispensers',
    ],
    useCases: [
      'Public restroom dispensers',
      'Office buildings',
      'Hospitals and clinics',
      'Schools and universities',
      'Shopping malls',
    ],
    seoTitle: 'C-Fold Tissue | Commercial Hand Towels for Dispensers',
    seoDescription: 'Commercial C-fold hand towels for dispensers. High capacity, cost-effective.',
  },

  // ==================== M-FOLD TISSUE ====================
  {
    id: 'm-fold-tissue-commercial',
    name: 'M-Fold Tissue (Commercial)',
    brand: 'SKYTION',
    category: 'm-fold',
    categoryLabel: 'M-Fold Tissue',
    subcategory: 'M-Fold Hand Towels',
    slug: 'm-fold-tissue',
    description: 'M-fold hand towels for commercial dispensers. Compact fold design for efficient dispensing.',
    images: [
      { src: '/assets/skytion/products/m-fold-tissue.png', alt: 'M-Fold Tissue' },
    ],
    specs: {
      ply: '1-2',
      fold: 'M-Fold',
      material: 'Virgin/Recycled Pulp',
      application: ['Commercial restrooms', 'Offices', 'Public facilities'],
    },
    features: [
      'M-fold compact design',
      'One-at-a-time dispensing',
      'Reduces waste',
      'Good absorbency',
    ],
    useCases: [
      'Commercial restrooms',
      'Office buildings',
      'Public facilities',
      'Healthcare centers',
    ],
    seoTitle: 'M-Fold Tissue | Commercial Hand Towels',
    seoDescription: 'M-fold hand towels for commercial dispensers. Compact design, reduces waste.',
  },

  // ==================== HRT ROLLS ====================
  {
    id: 'hrt-tissue-roll',
    name: 'HRT Tissue Paper Napkin Roll',
    brand: 'SKYTION',
    category: 'hrt-rolls',
    categoryLabel: 'HRT Rolls',
    subcategory: 'Hard Roll Tissue',
    slug: 'hrt-roll',
    description: 'Hard Roll Tissue (HRT) for commercial and industrial use. Large rolls for high-capacity dispensers.',
    images: [
      { src: '/assets/skytion/products/HTR-tissue-paper.jpg', alt: 'HRT Tissue Paper Rolls' },
    ],
    specs: {
      ply: '1-2',
      type: 'Hard Roll',
      material: 'Recycled/Virgin Pulp',
      application: ['Industrial', 'Commercial', 'Institutional'],
    },
    features: [
      'Large capacity rolls',
      'Cost-effective',
      'Suitable for industrial dispensers',
      'High durability',
    ],
    useCases: [
      'Industrial facilities',
      'Large commercial buildings',
      'Institutions',
      'Warehouses',
    ],
    seoTitle: 'HRT Tissue Paper Roll | Hard Roll for Commercial Use',
    seoDescription: 'Hard Roll Tissue (HRT) for commercial and industrial dispensers.',
  },

  // ==================== MINI POCKET TISSUES ====================
  {
    id: 'mini-pocket-tissue',
    name: 'Mini Pocket Tissue',
    brand: 'SKYTION',
    category: 'mini-tissue',
    categoryLabel: 'Mini Pocket Tissues',
    subcategory: 'Pocket Tissues',
    slug: 'mini-tissue',
    description: 'Compact mini pocket tissues perfect for on-the-go. Convenient packaging for travel and daily carry.',
    images: [
      { src: '/assets/skytion/products/skytion-mini-tissue-papers.png', alt: 'Mini Pocket Tissues' },
    ],
    specs: {
      ply: '2',
      material: 'Virgin Pulp',
      application: ['Travel', 'Daily carry', 'Outdoor activities'],
    },
    features: [
      'Compact size',
      'Portable packaging',
      'Soft and strong',
      'Hygienic',
    ],
    useCases: [
      'Travel',
      'Daily purse/bag carry',
      'Outdoor activities',
      'Emergency kit',
    ],
    seoTitle: 'Mini Pocket Tissue | Portable Travel Tissue',
    seoDescription: 'Compact mini pocket tissues for travel and daily carry. 2 ply, soft and strong.',
  },
  // ==================== PAPER CUPS - SINGLE WALL ====================
  {
    id: 'single-wall-paper-cup',
    name: 'Single Wall Paper Cups',
    brand: 'SKYTION',
    category: 'paper-cups',
    categoryLabel: 'Paper Cups',
    subcategory: 'Single Wall Cups',
    slug: 'single-wall-paper-cups',
    description: 'Economical single wall paper cups offering the perfect balance of cost-efficiency and durability. Ideal for high-volume service areas and budget-conscious operations. Made with food-grade PE coated paper with smooth rims for comfortable drinking.',
    images: [
      { src: '/assets/skytion/products/single-wall-paper-cup.png', alt: 'Single Wall Paper Cup' },
    ],
    specs: {
      ply: '1',
      material: 'Food-grade PE Coated Paper',
      type: 'Single Wall',
      application: ['Coffee shops', 'Tea service', 'Water dispensers', 'Office pantries', 'Events'],
    },
    features: [
      'Food-grade PE coated paper',
      'Available in custom prints',
      'Smooth rim for comfortable drinking',
      'Economical and cost-effective',
      'Ideal for high-volume service',
      'Sustainable packaging solution',
    ],
    useCases: [
      'Espresso servings (3 oz / 90 ml)',
      'Small coffee (6 oz / 180 ml)',
      'Regular beverages (8 oz / 240 ml)',
      'Medium drinks (12 oz / 350 ml)',
      'Large beverages (16 oz / 470 ml)',
    ],
    seoTitle: 'Single Wall Paper Cups | Wholesale Disposable Cups - SKYTION',
    seoDescription: 'Economical single wall paper cups in 3oz to 16oz sizes. Food-grade PE coated, custom prints available. Bulk orders, worldwide shipping.',
  },

  // ==================== PAPER CUPS - DOUBLE WALL ====================
  {
    id: 'double-wall-paper-cup',
    name: 'Double Wall Paper Cups',
    brand: 'SKYSUN',
    category: 'paper-cups',
    categoryLabel: 'Paper Cups',
    subcategory: 'Double Wall Cups',
    slug: 'double-wall-paper-cups',
    description: 'Premium double wall paper cups with superior heat protection. The double layer construction creates an insulating air gap, keeping beverages hotter for longer while remaining cool to the touch. No sleeve required.',
    images: [
      { src: '/assets/skytion/products/double-wall-paper-cup.png', alt: 'Double Wall Paper Cup' },
    ],
    specs: {
      ply: '2',
      material: 'Food-grade PE Coated Paper',
      type: 'Double Wall',
      application: ['Premium coffee shops', 'Hot beverages', 'Takeaway drinks', 'Cafes'],
    },
    features: [
      'Biodegradable material',
      'Double layer insulation',
      'Custom print available',
      'Food grade certified',
      'No sleeve required',
      'Superior heat protection',
      'Cool to the touch',
    ],
    useCases: [
      'Hot coffee (6 oz / 180 ml)',
      'Regular hot beverages (8 oz / 240 ml)',
      'Medium coffee (12 oz / 350 ml)',
      'Large drinks (16 oz / 470 ml)',
    ],
    seoTitle: 'Double Wall Paper Cups | Premium Insulated Coffee Cups - SKYSUN',
    seoDescription: 'Premium double wall paper cups with superior insulation. No sleeve needed. Available in 6oz to 16oz. Eco-friendly, biodegradable.',
  },

  // ==================== FOOD CONTAINERS - KRAFT ====================
  {
    id: 'kraft-food-containers',
    name: 'Kraft Food Containers',
    brand: 'SKYSUN',
    category: 'food-containers',
    categoryLabel: 'Food Containers',
    subcategory: 'Kraft Containers',
    slug: 'kraft-food-containers',
    description: 'Premium sustainable kraft food containers with natural kraft finish. Our signature unbleached finish provides an organic, earthy aesthetic while ensuring maximum durability for hot and cold food items. Oil and grease resistant, microwave and freezer safe.',
    images: [
      { src: '/assets/skytion/products/kraft-food-container.png', alt: 'Kraft Food Container' },
    ],
    specs: {
      ply: '1',
      material: 'Natural Kraft Paper',
      type: 'Food Container',
      application: ['Takeaway meals', 'Meal prep', 'Catering', 'Food delivery', 'Restaurants'],
    },
    features: [
      'Oil and grease resistant',
      'Microwave and freezer safe',
      '100% recyclable material',
      'Natural kraft finish',
      'Eco-friendly packaging',
      'Hot and cold food compatible',
      'Leak-resistant construction',
    ],
    useCases: [
      'Small portions (500ml / 16oz)',
      'Medium meals (750ml / 26oz)',
      'Large servings (1000ml / 32oz)',
      'Family meals (2000ml / 64oz)',
    ],
    seoTitle: 'Kraft Food Containers | Eco-Friendly Takeaway Containers - SKYSUN',
    seoDescription: 'Sustainable kraft food containers in 500ml to 2000ml. Microwave safe, oil resistant, 100% recyclable. Premium packaging for hot and cold foods.',
  },

  // ==================== FOOD CONTAINERS - PAPER ====================
  {
    id: 'paper-food-containers',
    name: 'Paper Food Containers',
    brand: 'SKYTION',
    category: 'food-containers',
    categoryLabel: 'Food Containers',
    subcategory: 'Paper Containers',
    slug: 'paper-food-containers',
    description: 'Eco-friendly disposable paper food containers designed for modern catering needs. Heavy-duty Kraft construction with leak-resistant PE lining. Ideal for both hot and cold contents, maintaining structural integrity even with greasy or liquid-heavy foods.',
    images: [
      { src: '/assets/skytion/products/paper-food-container.png', alt: 'Paper Food Container' },
    ],
    specs: {
      ply: '1',
      material: 'Kraft Paper with PE Lining',
      type: 'Food Container',
      application: ['Catering', 'Food delivery', 'Street food', 'Fast food', 'Events'],
    },
    features: [
      'Bio-degradable material',
      'Microwave safe',
      'Leak-resistant PE lining',
      'Heavy-duty construction',
      'Hot and cold food compatible',
      'Grease resistant',
      'Refrigerator safe',
    ],
    useCases: [
      'Sides and dips (8 oz)',
      'Soups and salads (16 oz)',
      'Main courses (32 oz)',
      'Bulk meals (64 oz)',
    ],
    seoTitle: 'Paper Food Containers | Disposable Eco-Friendly Containers - SKYTION',
    seoDescription: 'Eco-friendly paper food containers with leak-resistant lining. Available in 8oz to 64oz. Microwave safe, biodegradable, perfect for catering.',
  },

  // ==================== DINING TISSUE ====================
  {
    id: 'dining-tissue-paper',
    name: '2 Ply Dining Tissue',
    brand: 'SKYSUN',
    category: 'dining-tissue',
    categoryLabel: 'Dining Tissue',
    subcategory: 'Restaurant Tissue',
    slug: 'dining-tissue-paper',
    description: 'Premium soft dining tissue perfect for restaurants and food service. Made from high-quality virgin pulp for superior softness and absorbency. 30cm x 30cm sheet size, ideal for fine dining establishments.',
    images: [
      { src: '/assets/skytion/products/dining-tissue.png', alt: 'Dining Tissue Paper' },
    ],
    specs: {
      ply: '2',
      dimension: '30cm x 30cm',
      material: 'Soft Tissue - Virgin Pulp',
      application: ['Dining', 'Restaurant', 'Food service', 'Catering'],
    },
    features: [
      'Premium soft texture',
      'High absorbency',
      'Food safe certified',
      '2 ply construction',
      'Generous 30x30cm size',
      'Made from virgin pulp',
      'Elegant presentation',
    ],
    useCases: [
      'Fine dining restaurants',
      'Casual dining',
      'Catering services',
      'Hotel restaurants',
      'Food service industry',
    ],
    seoTitle: '2 Ply Dining Tissue | Premium Restaurant Napkins - SKYSUN',
    seoDescription: 'Premium 2 ply dining tissue (30x30cm) for restaurants and food service. Soft, absorbent, food safe. Made from high-quality virgin pulp.',
  },

  // ==================== HYGIENE SOLUTIONS - PAPER NAPKINS & ROLLS ====================
  {
    id: 'paper-napkins-rolls',
    name: 'Paper Napkins & Paper Rolls',
    brand: 'SKYTION',
    category: 'hygiene-solutions',
    categoryLabel: 'Hygiene Solutions',
    subcategory: 'Commercial Napkins',
    slug: 'paper-napkins-rolls',
    description: 'Premium quality hygiene and sanitation products for commercial and residential use. Complete range including M-Fold tissue, C-Fold tissue, and HRT (Hard Roll Towels) for automated dispensers. Eco-friendly, fast delivery, best value.',
    images: [
      { src: '/assets/skytion/products/paper-napkins-rolls.png', alt: 'Paper Napkins and Rolls' },
    ],
    specs: {
      ply: '1-2',
      material: 'Virgin/Recycled Pulp',
      type: 'Multi-Format',
      application: ['Commercial washrooms', 'Public restrooms', 'Offices', 'Healthcare', 'Industrial'],
    },
    features: [
      'Eco-friendly materials',
      'Premium quality certified',
      'Fast delivery available',
      'Best value pricing',
      'Superior absorbency',
      'Compatible with standard dispensers',
      'Multiple fold options',
    ],
    useCases: [
      'M-Fold tissue for high-traffic washrooms',
      'C-Fold tissue for classic dispensers',
      'HRT rolls for automated dispensers',
      'Industrial grade hygiene solutions',
    ],
    seoTitle: 'Paper Napkins & Rolls | Commercial Hygiene Solutions - SKYTION',
    seoDescription: 'Complete hygiene range: M-Fold, C-Fold tissue and HRT rolls. Eco-friendly, premium quality, bulk pricing for commercial and industrial use.',
  },

  // ==================== WOODEN CUTLERY ====================
  {
    id: 'wooden-cutlery-set',
    name: 'Wooden Cutlery Set',
    brand: 'SKYSUN',
    category: 'wooden-cutlery',
    categoryLabel: 'Wooden Cutlery',
    subcategory: 'Cutlery Sets',
    slug: 'wooden-cutlery-set',
    description: 'Eco-friendly wooden spoons, forks, and knives made from sustainable birch wood. Smooth finish with no splinters. 100% natural and biodegradable, perfect for environmentally conscious dining.',
    images: [
      { src: '/assets/skytion/products/wooden-cutlery.png', alt: 'Wooden Cutlery Set - Spoon Fork Knife' },
    ],
    specs: {
      ply: '1',
      material: 'Sustainable Birch Wood',
      type: 'Cutlery Set',
      application: ['Takeaway', 'Events', 'Restaurants', 'Catering', 'Picnics'],
    },
    features: [
      '100% natural and biodegradable',
      'Made from sustainable birch wood',
      'Smooth finish, no splinters',
      'Food safe certified',
      'Eco-friendly alternative to plastic',
      'Sturdy and durable',
    ],
    useCases: [
      'Wooden spoons for soups and desserts',
      'Wooden forks for salads and pasta',
      'Wooden knives with serrated edge',
      'Pre-wrapped complete sets for takeaway',
    ],
    seoTitle: 'Wooden Cutlery Set | Eco-Friendly Birch Wood Utensils - SKYSUN',
    seoDescription: '100% natural wooden cutlery set - spoons, forks, knives. Sustainable birch wood, biodegradable, food safe. Perfect for eco-conscious dining.',
  },
];

// Helper functions
export const getProductsByCategory = (categoryId: string): Product[] => {
  return products.filter((product) => product.category === categoryId);
};

export const getProductBySlug = (slug: string): Product | undefined => {
  return products.find((product) => product.slug === slug);
};

export const getProductById = (id: string): Product | undefined => {
  return products.find((product) => product.id === id);
};

export const getAllCategories = () => {
  return productCategories;
};

export const getUniqueCategories = (): string[] => {
  return ['All', ...productCategories.map(cat => cat.name)];
};

export const searchProducts = (query: string): Product[] => {
  const lowercaseQuery = query.toLowerCase();
  return products.filter(
    (product) =>
      product.name.toLowerCase().includes(lowercaseQuery) ||
      product.description.toLowerCase().includes(lowercaseQuery) ||
      product.features.some((f) => f.toLowerCase().includes(lowercaseQuery)) ||
      product.brand.toLowerCase().includes(lowercaseQuery)
  );
};

export default products;
